import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCompanyData } from '~/data/company';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useAuthentication } from '~/data/user/useAuthentication';
import {
  putEmailNotifyFrequency,
  useGetEmailNotificationFrequency,
} from '~/data/userSettings';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useVisible } from '~/hooks/useVisible';

type EmailNotificationFrequencyForm = {
  frequency: string;
};

export const useEmailNotificationCard = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { userDetails } = useAuthentication();
  const { companyData } = useCompanyData();
  const { getEnums } = useAllEnums();

  const { isLocked, frequencyOptions } = useMemo(() => {
    const isLocked =
      companyData?.config?.fComNotifyConfig?.fComDefaultEmailNotificationLock ??
      false;

    const frequencyOptions = getEnums(EnumListKey.EMAIL_NOTIFY_FREQUENCY);

    return {
      isLocked,
      frequencyOptions,
    };
  }, [companyData, getEnums]);

  const { isVisible, show, hide } = useVisible();

  const { data, isLoading, failedToLoad, mutate } =
    useGetEmailNotificationFrequency(
      {
        userName: userDetails?.username,
      },
      undefined,
      isLocked,
    );

  const form = useForm<EmailNotificationFrequencyForm>({
    defaultValues: {
      frequency: '',
    },
  });
  const { handleSubmit, reset } = form;

  const hasDataChanged = useHasChanged(data);
  useEffect(() => {
    if (hasDataChanged && !!data) {
      reset({ frequency: data });
    }
  }, [reset, hasDataChanged, data]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      return (
        !!userDetails?.username
          ? putEmailNotifyFrequency(userDetails.username, data.frequency)
          : Promise.reject('missingUserName')
      )
        .then(() => {
          mutate();
          hide();
          enqueueSnackbar(t('message_success_approval'), {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar(t('laErrorSavingData'), { variant: 'error' });
        });
    });
  }, [t, enqueueSnackbar, handleSubmit, mutate, hide, userDetails]);

  const onCancelEdit = useCallback(() => {
    reset({ frequency: data });
    hide();
  }, [reset, data, hide]);

  return {
    isLocked,
    isLoading,
    failedToLoad,
    form,
    editMode: isVisible,
    onEdit: show,
    onSubmit,
    onCancelEdit,
    frequencyOptions,
  };
};
